import { get, slice, map, orderBy, some, isNil } from "lodash"
import { createSelector } from "reselect"
import { getCurrency } from "modules/portfolio-root/selectors/portfolios"
import { makeCurrencyFormatter, numberFormatter } from "helpers/formatters/numbers"
import { formatLongDate } from "helpers/formatters/dates"

const EMPTY_OBJECT = {}
const EMPTY_ARRAY = []
export const getActiveTab = (state) => get(state, "modules.uploadScanPreview.activeTab")
export const getScanResult = (state, props) => get(props, "scanResult", EMPTY_OBJECT)
export const getSummary = (state, props) => get(props, "scanResult.summary", EMPTY_OBJECT)
export const getCoveragePage = (state) => get(state, "modules.uploadScanPreview.coveragePage")
export const getCoverageDate = (state) => get(state, "modules.uploadScanPreview.coverageDate")
export const getSecuritiesPage = (state) => get(state, "modules.uploadScanPreview.securitiesPage")
export const getCoverageSecuritiesPage = (state) => get(state, "modules.uploadScanPreview.coverageSecuritiesPage")
export const getNumberOfTableRows = (state) => get(state, "modules.uploadScanPreview.numberOfTableRows")
export const getUploadOption = (state, props) => get(props, "uploadOption")
export const getTargetName = (state, props) => get(props, "target.name")
export const getUploadDateTime = (state, props) => get(props, "uploadDateTime")

const getOrderedSecurities = createSelector(
  [getScanResult],
  (scanResult) => {
    const scanSecurities = get(scanResult, "scan.security", EMPTY_ARRAY)
    return orderBy(scanSecurities, [security => security.maxGMV, security => security.counts], ["desc", "desc"])
  },
)

export const getCoverage = createSelector(
  [getScanResult, getCoveragePage, getNumberOfTableRows, getCurrency],
  (scanResult, currentPage, numberOfTableRows, currency) => {
    const scanDates = get(scanResult, "scan.dates", EMPTY_ARRAY)

    if (scanDates.length > 0) {
      const scanSummary = get(scanResult, "scan.summary", EMPTY_ARRAY)
      const datesSubset = slice(scanDates, numberOfTableRows * currentPage, (numberOfTableRows * currentPage) + numberOfTableRows)

      const hasPnl = some(datesSubset, date => date.pnl)
      const hasEquity = !!scanSummary.averageEquity
      const dollarFormatter = makeCurrencyFormatter(currency)
      return {
        hasPnl,
        hasEquity,
        numberOfDates: scanDates.length,
        dates: map(datesSubset, date => ({
          ...date,
          formattedEquity: hasEquity ? dollarFormatter(date.equity) : null,
          formattedPnl: hasPnl ? dollarFormatter(date.pnl) : null,
          formattedDate: formatLongDate(date.date),
          percentUnmodeled: isNil(date.percentModeled) ? null : numberFormatter(100 - date.percentModeled),
          percentModeled: numberFormatter(date.percentModeled),
          percentUnmappedEquities: numberFormatter(date.percentUnmappedEquities),
        })),
      }
    }

    return null
  },
)

export const getSecurities = createSelector(
  [getOrderedSecurities, getSecuritiesPage, getNumberOfTableRows],
  (scanSecurities, securitiesPage, numberOfTableRows) => {
    const securitiesSubset = slice(scanSecurities, numberOfTableRows * securitiesPage, (numberOfTableRows * securitiesPage) + numberOfTableRows)
    return {
      numberOfSecurities: scanSecurities.length,
      securities: map(securitiesSubset, security => ({
        ...security,
        from: formatLongDate(security.from),
        to: formatLongDate(security.to),
        maxGMV: numberFormatter(security.maxGMV),
      })),
    }
  },
)

export const getCoverageSecurities = createSelector(
  [getScanResult, getCoverageSecuritiesPage, getCoverageDate, getNumberOfTableRows],
  (scanResult, securitiesPage, coverageDate, numberOfTableRows) => {
    if (!coverageDate) return {}

    const scanSecurities = get(scanResult, "scan.security", EMPTY_ARRAY)
    const rangeSecurities = scanSecurities.filter(security =>
      coverageDate >= security.from && coverageDate <= security.to,
    )

    const securitiesSubset = slice(rangeSecurities, numberOfTableRows * securitiesPage, (numberOfTableRows * securitiesPage) + numberOfTableRows)
    return {
      numberOfSecurities: rangeSecurities.length,
      securities: map(securitiesSubset, security => ({
        ...security,
        from: formatLongDate(security.from),
        to: formatLongDate(security.to),
        maxGMV: numberFormatter(security.maxGMV),
      })),
    }
  },
)
